import { useState, useCallback, useMemo, useEffect } from 'react';
import { BASE_FOODS } from '../utils/constants';
import { calculateGL, calculateMealGL } from '../utils/calculations';
import { getCategory } from '../utils/categories';
import { filterFoods, generateFoodId, validateFood, exportDataAsJSON, showNotification } from '../utils/helpers';

/**
 * Custom hook for managing foods and meal calculations
 * @returns {Object} Food management state and methods
 */
export const useFoodManager = () => {
  const [foods, setFoods] = useState(() => {
    // Migration silencieuse : charger depuis BASE_FOODS,
    // puis fusionner avec le localStorage si existant
    const saved = localStorage.getItem('glycemic_foods');
    if (saved) {
      try {
        const localFoods = JSON.parse(saved);
        // Ajouter les aliments master manquants sans toucher aux données utilisateur
        const localIds = new Set(localFoods.map(f => f.id));
        const missingMaster = BASE_FOODS.filter(bf => !localIds.has(bf.id));
        return [...localFoods, ...missingMaster];
      } catch {
        return BASE_FOODS;
      }
    }
    return BASE_FOODS;
  });

  // Persister les aliments dans le localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('glycemic_foods', JSON.stringify(foods));
  }, [foods]);
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('toutes');

  /**
   * Add a new food to the list
   * @param {Object} newFood - Food object to add
   * @returns {boolean} Success status
   */
  const addFood = useCallback((newFood) => {
    try {
      // Validate food data
      const validation = validateFood(newFood);
      if (!validation.isValid) {
        showNotification(`Erreur de validation: ${validation.errors.join(', ')}`, 'error');
        return false;
      }

      // Calculate GL and category if not provided
      const gl = newFood.gl || calculateGL(newFood.gi, newFood.carbs);
      const category = newFood.category || getCategory(gl);
      
      const foodToAdd = {
        id: newFood.id || generateFoodId(foods),
        name: newFood.name.trim(),
        gi: parseFloat(newFood.gi),
        carbs: parseFloat(newFood.carbs),
        gl,
        category,
        source: newFood.source || 'manuel',
        brand: newFood.brand || '',
        nutriscore: newFood.nutriscore || '',
        barcode: newFood.barcode || '',
        image: newFood.image || ''
      };

      setFoods(prevFoods => [...prevFoods, foodToAdd]);
      showNotification(`Aliment ajouté : ${foodToAdd.name}`, 'success');
      return true;
    } catch (error) {
      console.error('Error adding food:', error);
      showNotification('Erreur lors de l\'ajout de l\'aliment', 'error');
      return false;
    }
  }, [foods]);

  /**
   * Remove a food from the list
   * @param {number} foodId - ID of food to remove
   */
  const removeFood = useCallback((foodId) => {
    try {
      setFoods(prevFoods => prevFoods.filter(food => food.id !== foodId));
      // Also remove from selected foods if present
      setSelectedFoods(prevSelected => prevSelected.filter(food => food.id !== foodId));
      showNotification('Aliment supprimé', 'success');
    } catch (error) {
      console.error('Error removing food:', error);
      showNotification('Erreur lors de la suppression', 'error');
    }
  }, []);

  /**
   * Update an existing food
   * @param {number} foodId - ID of food to update
   * @param {Object} updatedData - Updated food data
   */
  const updateFood = useCallback((foodId, updatedData) => {
    try {
      setFoods(prevFoods => 
        prevFoods.map(food => {
          if (food.id === foodId) {
            const gl = calculateGL(updatedData.gi || food.gi, updatedData.carbs || food.carbs);
            const category = getCategory(gl);
            
            return {
              ...food,
              ...updatedData,
              gl,
              category
            };
          }
          return food;
        })
      );
      showNotification('Aliment mis à jour', 'success');
    } catch (error) {
      console.error('Error updating food:', error);
      showNotification('Erreur lors de la mise à jour', 'error');
    }
  }, []);

  /**
   * Toggle food selection for meal calculator
   * @param {Object} food - Food to toggle
   */
  const toggleFoodSelection = useCallback((food) => {
    try {
      const exists = selectedFoods.find(f => f.id === food.id);
      
      if (exists) {
        setSelectedFoods(prevSelected => prevSelected.filter(f => f.id !== food.id));
      } else {
        setSelectedFoods(prevSelected => [
          ...prevSelected, 
          { ...food, quantity: 100 }
        ]);
      }
    } catch (error) {
      console.error('Error toggling food selection:', error);
      showNotification('Erreur lors de la sélection', 'error');
    }
  }, [selectedFoods]);

  /**
   * Update food quantity in meal
   * @param {number} foodId - ID of food to update
   * @param {number} quantity - New quantity
   */
  const updateFoodQuantity = useCallback((foodId, quantity) => {
    try {
      const numericQuantity = parseFloat(quantity) || 0;
      
      setSelectedFoods(prevSelected =>
        prevSelected.map(food =>
          food.id === foodId 
            ? { ...food, quantity: numericQuantity }
            : food
        )
      );
    } catch (error) {
      console.error('Error updating food quantity:', error);
      showNotification('Erreur lors de la mise à jour de la quantité', 'error');
    }
  }, []);

  /**
   * Clear all selected foods
   */
  const clearSelectedFoods = useCallback(() => {
    setSelectedFoods([]);
  }, []);

  /**
   * Load foods data (e.g., from backup)
   * @param {Array} newFoods - Foods to load
   * @param {Array} newSelectedFoods - Selected foods to load (optional)
   */
  const loadFoods = useCallback((newFoods, newSelectedFoods = []) => {
    try {
      if (Array.isArray(newFoods)) {
        setFoods(newFoods);
      }
      if (Array.isArray(newSelectedFoods)) {
        setSelectedFoods(newSelectedFoods);
      }
      showNotification('Données chargées avec succès', 'success');
    } catch (error) {
      console.error('Error loading foods:', error);
      showNotification('Erreur lors du chargement des données', 'error');
    }
  }, []);

  /**
   * Reset to base foods - Supprime tous les aliments personnalisés,
   * recharge EXCLUSIVEMENT la liste BASE_FOODS de constants.js
   */
  const resetToBaseFoods = useCallback(() => {
    setFoods([...BASE_FOODS]);
    setSelectedFoods([]);
    localStorage.removeItem('glycemic_foods');
    showNotification('Données réinitialisées aux aliments officiels', 'info');
  }, []);

  /**
   * Export foods data
   * @param {string} filename - Export filename
   */
  const exportFoods = useCallback((filename) => {
    try {
      exportDataAsJSON(foods, filename);
      showNotification('Données exportées avec succès', 'success');
    } catch (error) {
      console.error('Error exporting foods:', error);
      showNotification('Erreur lors de l\'export', 'error');
    }
  }, [foods]);

  // Computed values
  const filteredFoods = useMemo(() => {
    return filterFoods(foods, searchTerm, selectedCategory);
  }, [foods, searchTerm, selectedCategory]);

  const totalMealGL = useMemo(() => {
    return calculateMealGL(selectedFoods);
  }, [selectedFoods]);

  const mealCategory = useMemo(() => {
    return getCategory(totalMealGL);
  }, [totalMealGL]);

  const isSelectedFood = useCallback((foodId) => {
    return selectedFoods.some(food => food.id === foodId);
  }, [selectedFoods]);

  const getSelectedFood = useCallback((foodId) => {
    return selectedFoods.find(food => food.id === foodId);
  }, [selectedFoods]);

  const foodStats = useMemo(() => {
    const totalFoods = foods.length;
    const baseFoodsCount = BASE_FOODS.length;
    const addedFoods = totalFoods - baseFoodsCount;
    
    const categoryCounts = foods.reduce((acc, food) => {
      acc[food.category] = (acc[food.category] || 0) + 1;
      return acc;
    }, {});

    const sourceCounts = foods.reduce((acc, food) => {
      acc[food.source] = (acc[food.source] || 0) + 1;
      return acc;
    }, {});

    return {
      total: totalFoods,
      added: addedFoods,
      selected: selectedFoods.length,
      categories: categoryCounts,
      sources: sourceCounts
    };
  }, [foods, selectedFoods]);

  return {
    // State
    foods,
    selectedFoods,
    searchTerm,
    selectedCategory,
    
    // Setters
    setSearchTerm,
    setSelectedCategory,
    
    // Food management
    addFood,
    removeFood,
    updateFood,
    loadFoods,
    resetToBaseFoods,
    exportFoods,
    
    // Selection management
    toggleFoodSelection,
    updateFoodQuantity,
    clearSelectedFoods,
    
    // Computed values
    filteredFoods,
    totalMealGL,
    mealCategory,
    foodStats,
    
    // Helper methods
    isSelectedFood,
    getSelectedFood,
    
    // Constants
    baseFoods: BASE_FOODS
  };
};